import { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Animated, Easing, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import {
  DEFAULT_AVATARS,
  RESULT_COPY,
  CARD_FRAME_COLORS,
} from './GameResultModal.constants';
import { getGameResultSlideStyles } from './GameResultSlide.styles';
import { getAvatarSourcesForOutcome } from './gameResultModal.helper';
import type { GameResultModalAvatarId } from './gameResultModal.helper';
import type { GameResultItem } from './gameResultModal.types';
import { LayeredAvatarView } from '../../avatars/LayeredAvatarLab/LayeredAvatarView';
import { collectEquippedLayers, canRenderLayeredAvatar } from '../../avatars/LayeredAvatarLab/characterCustomizer.helper';
import { CountUp } from './CountUp';
import { StatBar } from './StatBar';

export interface GameResultSlideProps {
  item: GameResultItem;
  slideWidth?: number;
  slideHeight: number;
  cardWidth: number;
  titleScale: Animated.Value;
  cardTranslateY: Animated.Value;
  coinScale: Animated.Value;
  slideIndex: number;
  totalSlides: number;
}

export function GameResultSlide({
  item,
  slideWidth,
  slideHeight,
  cardWidth,
  titleScale,
  cardTranslateY,
  coinScale,
  slideIndex,
  totalSlides,
}: GameResultSlideProps) {
  const outcome = item.outcome;
  const isWon = outcome === 'won';
  const isDraw = outcome === 'draw';
  const frame = CARD_FRAME_COLORS[isWon ? 'won' : isDraw ? 'draw' : 'lost'];

  const styles = useMemo(
    () => getGameResultSlideStyles(frame, cardWidth),
    [frame, cardWidth],
  );

  const homeAvatarId = item.homeAvatarId ?? DEFAULT_AVATARS.HOME;
  const awayAvatarId = item.awayAvatarId ?? DEFAULT_AVATARS.AWAY;

  const homeSources = useMemo(
    () => getAvatarSourcesForOutcome(homeAvatarId as GameResultModalAvatarId, isWon || isDraw),
    [homeAvatarId, isWon, isDraw],
  );

  const awaySources = useMemo(
    () => getAvatarSourcesForOutcome(awayAvatarId as GameResultModalAvatarId, !isWon || isDraw),
    [awayAvatarId, isWon, isDraw],
  );

  // onError-based source cycling: sad → angry → idle, immune to CDN non-404 failures
  const [homeIdx, setHomeIdx] = useState(0);
  const [awayIdx, setAwayIdx] = useState(0);
  useEffect(() => { setHomeIdx(0); }, [homeSources]);
  useEffect(() => { setAwayIdx(0); }, [awaySources]);

  // Custom layered look wins over the preset avatar — but only when it resolves to at least
  // one drawable layer (an empty/unknown config falls back to the preset path, never blank).
  const homeConfig = useMemo(
    () => (item.homeAvatarConfig && collectEquippedLayers(item.homeAvatarConfig).length > 0
      ? item.homeAvatarConfig
      : null),
    [item.homeAvatarConfig],
  );
  const awayConfig = useMemo(
    () => (item.awayAvatarConfig && collectEquippedLayers(item.awayAvatarConfig).length > 0
      ? item.awayAvatarConfig
      : null),
    [item.awayAvatarConfig],
  );

  const showPoints = item.yourPoints != null && item.rivalPoints != null;
  const showHits = item.yourCorrectGuesses != null && item.rivalCorrectGuesses != null;
  const homeLabel = item.homeName ?? RESULT_COPY.LABEL_YOU;
  const awayLabel = item.awayName ?? RESULT_COPY.LABEL_RIVAL;

  const footerRight = totalSlides > 1
    ? `${String(slideIndex + 1).padStart(2, '0')} / ${String(totalSlides).padStart(2, '0')}`
    : undefined;

  // ── Holo sweep animation (loops) ──────────────────────────────────────────
  const holoAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(holoAnim, {
          toValue: 1,
          duration: 2600,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.delay(1400),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [holoAnim]);

  const holoTranslateX = holoAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-cardWidth * 0.6, cardWidth],
  });

  // ── Win bounce animation (loops while won) ─────────────────────────────────
  const bounceAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!isWon) {
      bounceAnim.setValue(0);
      return;
    }
    const bounce = Animated.loop(
      Animated.sequence([
        Animated.timing(bounceAnim, {
          toValue: -9,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(bounceAnim, {
          toValue: 0,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    bounce.start();
    return () => bounce.stop();
  }, [isWon, bounceAnim]);

  return (
    <View
      style={
        slideWidth !== undefined
          ? [styles.containerCentered, { width: slideWidth, height: slideHeight }]
          : styles.containerFlex
      }
    >
      {/* Stage radial glow */}
      <View pointerEvents="none" style={[StyleSheet.absoluteFillObject, styles.stageGlowWrap]}>
        <View style={styles.stageGlowCircle} />
      </View>

      {/* Card — entry animation: scale + fly-up */}
      <Animated.View
        style={[
          styles.cardWrap,
          { transform: [{ scale: titleScale }, { translateY: cardTranslateY }] },
        ]}
      >
        {/* Outer gradient frame */}
        <LinearGradient
          colors={frame.frameGradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.cardFrame}
        >
          {/* Inner border */}
          <View style={styles.cardInnerBorder}>
            {/* Card body */}
            <View style={styles.cardBody}>

              {/* Holo sweep overlay — diagonal slash sweeps across card */}
              <Animated.View
                pointerEvents="none"
                style={[
                  styles.holoStrip,
                  { transform: [{ translateX: holoTranslateX }, { skewX: '-14deg' }] },
                ]}
              >
                <LinearGradient
                  colors={frame.holoColors as unknown as [string, string, ...string[]]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={StyleSheet.absoluteFillObject}
                />
              </Animated.View>

              {/* ── Title plate ── */}
              <LinearGradient
                colors={frame.plateGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={styles.titlePlate}
              >
                <Text style={styles.cardTitle}>{frame.cardTitle}</Text>
              </LinearGradient>

              {/* ── Portrait ── */}
              <View style={styles.portrait}>
                <Text style={[styles.cornerStar, styles.cornerTL]}>✦</Text>
                <Text style={[styles.cornerStar, styles.cornerTR]}>✦</Text>
                <Text style={[styles.cornerStar, styles.cornerBL]}>✦</Text>
                <Text style={[styles.cornerStar, styles.cornerBR]}>✦</Text>

                {/* Main avatar with win bounce — layered custom look or preset portrait */}
                <Animated.View style={{ transform: [{ translateY: bounceAnim }] }}>
                  {homeConfig && canRenderLayeredAvatar(homeConfig) ? (
                    <LayeredAvatarView equipped={homeConfig} size={122} rounded />
                  ) : (
                    <Image
                      source={homeSources[homeIdx] ?? null}
                      style={styles.portraitAvatar}
                      contentFit="cover"
                      onError={() => setHomeIdx(i => Math.min(i + 1, homeSources.length - 1))}
                      alt=""
                    />
                  )}
                </Animated.View>

                {/* Home name label */}
                <Text style={styles.homeLabel}>{homeLabel}</Text>

                {/* Rival circle — layered custom look or preset portrait */}
                <View style={styles.rivalCircle}>
                  {awayConfig && canRenderLayeredAvatar(awayConfig) ? (
                    <LayeredAvatarView equipped={awayConfig} size={46} rounded />
                  ) : (
                    <Image
                      source={awaySources[awayIdx] ?? null}
                      style={styles.rivalAvatar}
                      contentFit="cover"
                      onError={() => setAwayIdx(i => Math.min(i + 1, awaySources.length - 1))}
                      alt=""
                    />
                  )}
                </View>
                <Text style={styles.vsLabel}>vs {awayLabel}</Text>
              </View>

              {/* ── Subtitle ── */}
              <Text style={styles.cardSubtitle}>{frame.subtitle}</Text>

              {/* ── Stats ── */}
              {(showPoints || showHits) && (
                <View style={styles.statsSection}>
                  {showPoints && (
                    <StatBar
                      label={RESULT_COPY.SCORE_LABEL}
                      you={item.yourPoints!}
                      rival={item.rivalPoints!}
                      isDraw={isDraw}
                      highlightColor={frame.frame}
                    />
                  )}
                  {showHits && (
                    <StatBar
                      label={RESULT_COPY.HITS_LABEL}
                      you={item.yourCorrectGuesses!}
                      rival={item.rivalCorrectGuesses!}
                      isDraw={isDraw}
                      highlightColor={frame.frame}
                    />
                  )}
                </View>
              )}

              {/* ── Payout chip (pops in + count-up) ── */}
              <Animated.View
                style={[styles.payoutChip, { transform: [{ scale: coinScale }] }]}
              >
                <Text style={styles.payoutLabel}>Payout</Text>
                <View style={styles.payoutRow}>
                  <Image
                    source={IDLE_COIN_SOURCE}
                    style={styles.coinIcon}
                    contentFit="contain"
                    alt=""
                  />
                  <Text style={styles.payoutAmount}>
                    {frame.coinSign}
                    <CountUp value={item.coins} />
                  </Text>
                </View>
              </Animated.View>

              {/* ── Footer ── */}
              <View style={styles.cardFooter}>
                <Text style={styles.footerText}>Match Card</Text>
                {footerRight != null ? (
                  <Text style={styles.footerText}>{footerRight}</Text>
                ) : null}
              </View>

            </View>
          </View>
        </LinearGradient>
      </Animated.View>
    </View>
  );
}
